"use client";

import { api } from "@convex/_generated/api";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { ChevronDown, Crosshair, Plus } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  BUILDS_ITEMS,
  CATEGORY_ORDER,
  COMMUNITY_ITEMS,
  DATABASE_ITEMS,
  FILTER_ITEMS,
} from "@/lib/navbar-constants";
import { cn } from "@/lib/utils";
import { AdminNav } from "./admin/admin-nav";
import { Button } from "./ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { UserInfo } from "./user-info";

type MobileMenuProps = {
  userButton: React.ReactNode;
};

export function MobileMenu({ userButton }: MobileMenuProps) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [weaponsOpen, setWeaponsOpen] = useState(true);
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    {}
  );
  const weapons = useQuery(api.loadouts.queries.getWeapons);

  function isActive(href: string) {
    if (href === "/") {
      return pathname === "/";
    }
    return pathname?.startsWith(href);
  }

  function handleLinkClick() {
    setOpen(false);
  }

  function toggleCategory(category: string) {
    setOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }

  // Group weapons by category
  type Weapon = FunctionReturnType<
    typeof api.loadouts.queries.getWeapons
  >[number];
  const weaponsByCategory = weapons?.reduce(
    (acc, weapon) => {
      if (!acc[weapon.category]) {
        acc[weapon.category] = [];
      }
      acc[weapon.category].push(weapon);
      return acc;
    },
    {} as Record<string, Weapon[]>
  );

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button
          aria-label="Toggle menu"
          className="rounded-[5px] border-[#33383C] text-[#EAEBEC] hover:border-[#F5A23E] hover:bg-transparent lg:hidden"
          size="icon"
          variant="outline"
        >
          <svg
            aria-label="Menu icon"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Menu</title>
            <path
              d="M4 6h16M4 12h16M4 18h16"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </SheetTrigger>
      <SheetContent
        className="w-[300px] overflow-y-auto border-[#1c1f22] bg-[#0C0D0E] px-2 sm:w-[400px]"
        side="right"
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-[#EAEBEC] uppercase tracking-wide">
            Menu
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Create Button */}
          <Button
            asChild
            className="w-full rounded-[5px] bg-[#F5A23E] font-heading text-[#0a0b0c] uppercase tracking-wider hover:bg-[#ffbb63]"
            size="lg"
          >
            <Link
              data-s-event="navbar-mobile-create-button"
              href="/create"
              onClick={handleLinkClick}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Content
            </Link>
          </Button>

          <nav className="space-y-6">
            {/* Weapons Section */}
            <Collapsible onOpenChange={setWeaponsOpen} open={weaponsOpen}>
              <div className="space-y-2">
                <CollapsibleTrigger className="flex w-full items-center justify-between font-mono text-[#6a7075] text-xs uppercase tracking-[0.2em] hover:text-[#EAEBEC]">
                  <span>Weapons</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform",
                      weaponsOpen && "rotate-180"
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3">
                  <Link
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors",
                      isActive("/weapons") && !pathname?.includes("/weapons/")
                        ? "bg-[#141618] text-[#F5A23E]"
                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                    )}
                    data-s-event="navbar-mobile-weapons-all"
                    href="/weapons"
                    onClick={handleLinkClick}
                  >
                    <Crosshair className="h-4 w-4" />
                    All Weapons
                  </Link>

                  {/* Category Collapsibles */}
                  {weaponsByCategory &&
                    CATEGORY_ORDER.filter(
                      (category) => weaponsByCategory[category]
                    ).map((category) => {
                      const categoryWeapons = weaponsByCategory[category];
                      const isCategoryOpen = Boolean(openCategories[category]);

                      return (
                        <Collapsible
                          key={category}
                          onOpenChange={() => toggleCategory(category)}
                          open={isCategoryOpen}
                        >
                          <div className="space-y-1">
                            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 font-medium text-[#A9AFB3] text-sm transition-colors hover:bg-[#141618] hover:text-[#EAEBEC]">
                              <span>{category}</span>
                              <ChevronDown
                                className={cn(
                                  "h-3 w-3 transition-transform",
                                  isCategoryOpen && "rotate-180"
                                )}
                              />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-1 pl-3">
                              {categoryWeapons
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((weapon) => (
                                  <Link
                                    className={cn(
                                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                                      isActive(`/weapons/${weapon.slug}`)
                                        ? "bg-[#141618] text-[#F5A23E]"
                                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                                    )}
                                    data-s-event={`navbar-mobile-weapon-${weapon.name
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")}`}
                                    href={`/weapons/${weapon.slug}`}
                                    key={weapon._id}
                                    onClick={handleLinkClick}
                                  >
                                    {weapon.name}
                                  </Link>
                                ))}
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      );
                    })}
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* Builds Section */}
            <div>
              <h3 className="mb-2 font-mono text-[#6a7075] text-xs uppercase tracking-[0.2em]">
                Builds
              </h3>
              <div className="space-y-1">
                {BUILDS_ITEMS.map((item) => (
                  <Link
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors",
                      isActive(item.url)
                        ? "bg-[#141618] text-[#F5A23E]"
                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                    )}
                    data-s-event={`navbar-mobile-${item.event?.replace("navbar-builds-", "")}`}
                    href={item.url}
                    key={item.title}
                    onClick={handleLinkClick}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Database Section */}
            <div>
              <h3 className="mb-2 font-mono text-[#6a7075] text-xs uppercase tracking-[0.2em]">
                Database
              </h3>
              <div className="space-y-1">
                {DATABASE_ITEMS.map((item) => (
                  <Link
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors",
                      isActive(item.url)
                        ? "bg-[#141618] text-[#F5A23E]"
                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                    )}
                    data-s-event={`navbar-mobile-${item.event?.replace("navbar-database-", "")}`}
                    href={item.url}
                    key={item.title}
                    onClick={handleLinkClick}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Discover Section */}
            <div>
              <h3 className="mb-2 font-mono text-[#6a7075] text-xs uppercase tracking-[0.2em]">
                Discover
              </h3>
              <div className="space-y-1">
                {FILTER_ITEMS.map((item) => (
                  <Link
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors",
                      isActive(item.url)
                        ? "bg-[#141618] text-[#F5A23E]"
                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                    )}
                    data-s-event={`navbar-mobile-${item.event?.replace("navbar-discover-", "")}`}
                    href={item.url}
                    key={item.title}
                    onClick={handleLinkClick}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                ))}
                {COMMUNITY_ITEMS.map((item) => (
                  <Link
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors",
                      isActive(item.url)
                        ? "bg-[#141618] text-[#F5A23E]"
                        : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                    )}
                    data-s-event={`navbar-mobile-${item.event?.replace("navbar-community-", "")}`}
                    href={item.url}
                    key={item.title}
                    onClick={handleLinkClick}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* User Section */}
            <div className="border-[#1c1f22] border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AdminNav />
                  <UserInfo />
                </div>
                {userButton}
              </div>
            </div>
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
}
